import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, MailCheck } from "lucide-react";

const H_LOGO_URL =
  "/brand/hireloop-h.png";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("Check your inbox");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="forgot-password-page"
      className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md flex-col justify-center px-6 py-16"
    >
      <div className="flex flex-col items-center text-center">
        <img
          src={H_LOGO_URL}
          alt="Hireloop PRO"
          className="h-16 w-16 object-contain"
          style={{ mixBlendMode: "multiply" }}
        />
        <div className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
          Account recovery
        </div>
        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
          Forgot your password?
        </h1>
        <p className="mt-3 max-w-sm text-sm text-black">
          Enter the email address on your account and we'll send you a secure link to choose a new password. The link expires in 60 minutes.
        </p>
      </div>

      {sent ? (
        <div
          data-testid="forgot-password-success"
          className="mt-10 flex flex-col items-center gap-3 border border-emerald-200 bg-emerald-50 px-6 py-8 text-center"
        >
          <MailCheck className="h-8 w-8 text-emerald-700" />
          <h2 className="font-heading text-lg font-semibold text-emerald-900">
            Email on its way
          </h2>
          <p className="text-sm text-emerald-900">
            If an account exists for <span className="font-mono">{email}</span>, a password-reset link has been sent. Check your inbox (and spam folder).
          </p>
          <Button
            data-testid="forgot-password-back-to-login"
            onClick={() => navigate("/login")}
            variant="outline"
            className="mt-2 rounded-sm border-emerald-300"
          >
            Back to sign in
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-10 space-y-5">
          <div>
            <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-black">
              Email
            </Label>
            <Input
              id="email"
              data-testid="forgot-password-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 rounded-sm border-slate-300"
            />
          </div>
          <Button
            type="submit"
            data-testid="forgot-password-submit-btn"
            disabled={loading}
            className="w-full rounded-sm bg-blue-700 py-6 hover:bg-blue-600"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send reset link
          </Button>
        </form>
      )}

      <p className="mt-8 text-center text-sm text-slate-500">
        Remembered it?{" "}
        <Link
          to="/login"
          data-testid="forgot-password-to-login-link"
          className="font-medium text-blue-700 hover:underline"
        >
          Back to sign in
        </Link>
      </p>
    </main>
  );
}
